import type { JsonValue, SyncAction } from '../domain/resources';

export interface JsonChange {
  path: string[];
  action: SyncAction;
  value?: JsonValue;
}

export function cloneJson<T extends JsonValue>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isPlainObject(value: JsonValue | undefined): value is Record<string, JsonValue> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function jsonEquals(left: JsonValue | undefined, right: JsonValue | undefined): boolean {
  if (left === right) return true;
  if (left === undefined || right === undefined) return false;
  return JSON.stringify(left) === JSON.stringify(right);
}

export function diffJson(
  previous: JsonValue | undefined,
  next: JsonValue | undefined,
  path: string[] = []
): JsonChange[] {
  if (jsonEquals(previous, next)) return [];

  if (next === undefined) return [{ path, action: 'delete' }];
  if (previous === undefined) return [{ path, action: 'set', value: cloneJson(next) }];

  if (isPlainObject(previous) && isPlainObject(next)) {
    const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
    return [...keys].flatMap(key => diffJson(previous[key], next[key], [...path, key]));
  }

  // Arrays are intentionally atomic. Most app arrays represent ordered logs,
  // where an index-level merge would be more surprising than replacing the list.
  return [{ path, action: 'set', value: cloneJson(next) }];
}

export function applyJsonChange(
  current: JsonValue | undefined,
  change: JsonChange
): JsonValue | undefined {
  if (change.path.length === 0) {
    return change.action === 'delete' ? undefined : cloneJson(change.value ?? null);
  }

  const root: Record<string, JsonValue> = isPlainObject(current)
    ? cloneJson(current)
    : {};
  let cursor: Record<string, JsonValue> = root;

  for (let index = 0; index < change.path.length - 1; index += 1) {
    const segment = change.path[index];
    const child = cursor[segment];
    cursor[segment] = isPlainObject(child) ? cloneJson(child) : {};
    cursor = cursor[segment] as Record<string, JsonValue>;
  }

  const leaf = change.path[change.path.length - 1];
  if (change.action === 'delete') {
    delete cursor[leaf];
  } else {
    cursor[leaf] = cloneJson(change.value ?? null);
  }
  return root;
}
